'use strict';

/**
 * Service: Oauth2 HTTP
 *
 * @version
 * 2.47.0 LIBENV-219 Refactor bcxOauth2HttpRequest to support specifying $http config parameter
 * 1.30.0 LIBENV-95 Create new Oauth2HttpService to handle native http responses
 *
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Deitric Jang <djang@buildcentrix.com>
 * @copyright (c) 2021-2023 Build Centrix
 */
angular.module('bcx.service.oauth2http', ['wd.helper.configuration', 'wd.helper.oauth2http', 'wd.helper.users'])
    .factory('bcxOauth2HttpService',
        ['$q', 'wdConfigurationHelper', 'bcxHttpService', 'wdOauth2HttpHelper',
            function ($q, wdConfigurationHelper, bcxHttpService, wdOauth2HttpHelper) {

                /**
                 * @var {Object} service - Used to hold public functions
                 *
                 * @since 1.30.0
                 */
                var service = {};

                var oauth2Url = '';
                var singleSignOnUrl = '';
                var wdSettingsKey = 'wd.settings';
                var wdSettingsVersion = '1.0.2';

                service.cookieOptions = {};
                service.cookieAdapter = {};

                /**
                 * Add Authorization Bearer Header
                 *
                 * @since 1.30.0
                 * @param {String} accessToken
                 * @return {Boolean}
                 */
                function addAuthorizationBearerHeader(accessToken) {

                    var result = false;

                    if (accessToken) {

                        // Note: the old way does not use the correct syntax...
                        bcxHttpService.addHeader('Bearer', accessToken);

                        // However, the correct syntax causes CORS errors - @todo Investigate, Fix and Re-enable
                        // https://tools.ietf.org/html/rfc6750#section-2.1
                        // result = bcxHttpService.addHeader('Authorization', 'Bearer ' + accessToken);

                    }

                    return result;

                }

                /******************************************************************************************************/

                /**
                 * Directly Add Headers through the bcxHttpService Service
                 *
                 * @since 1.30.0
                 * @param {String} key
                 * @param {String} value
                 * @returns {Boolean}
                 */
                service.addHeader = function(key, value) {

                    return bcxHttpService.addHeader(key, value);

                };

                /**
                 * Delete Headers through the bcxHttpService Service
                 *
                 * @since 1.30.0
                 * @param {String} key
                 * @returns {Boolean}
                 */
                service.deleteHeader = function (key) {

                    return bcxHttpService.deleteHeader(key);

                };

                /**
                 * Set Headers through the bcxHttpService Service
                 *
                 * @since 1.30.0
                 * @param {String} key
                 * @param {String} value
                 * @returns {Boolean}
                 */
                service.updateHeader = function (key, value) {

                    return bcxHttpService.updateHeader(key, value);

                };

                /**
                 * Initialize Configuration
                 *
                 * @since 1.30.0
                 * @return {Q.Promise}
                 */
                service.initialize = function() {

                    var deferred = $q.defer();

                    var oauth2Url = wdConfigurationHelper.getOauth2AuthenticationUrl();
                    var cookieDomain = wdConfigurationHelper.getCookieDomain();
                    var singleSignOnUrl = wdConfigurationHelper.getOauth2SsoUrl();
                    var oauth2CookieOptions = {};
                    var userInfoCookieOptions = {};

                    // Setup oauth2 cookie
                    // The cookie domain is set on the PHP side in classic Environment::getOauth2CookieDomain()
                    // We need to set the oauth2CookieDomain here for when the access token is refreshed
                    oauth2CookieOptions.domain = cookieDomain;

                    service.setAccessTokenHeader();
                    service.setOauth2Url(oauth2Url);
                    service.setSingleSignOnUrl(singleSignOnUrl);
                    service.setOauth2CookieOptions(oauth2CookieOptions);

                    // Setup Webduct settings cookie
                    userInfoCookieOptions.domain = cookieDomain;
                    service.setUserInfoCookieOptions(userInfoCookieOptions);

                    service.cookieAdapter = new WebductCookieAdapter(Cookies);

                    deferred.resolve(service);

                    return deferred.promise;

                };

                /**
                 * Set Access Token Header
                 *
                 * @since 1.30.0
                 * @return {Boolean}
                 */
                service.setAccessTokenHeader = function() {

                    return addAuthorizationBearerHeader(wdOauth2HttpHelper.getAccessToken());

                };

                /**
                 * Update Local Oauth2Info after access token is refreshed
                 *
                 * @since 1.30.0
                 * @params {Object} response
                 * @return {Boolean}
                 */
                service.updateOauth2Info = function(response) {

                    // Set any fields in the oauth2_info that were not included in
                    // the response from oauth2 server after refreshing token.  This is needed for future token refreshes.

                    // Refresh token
                    /* istanbul ignore else */
                    if (typeof response.data.refresh_token === 'undefined') {
                        response.data.refresh_token = service.getRefreshToken();
                    }

                    // Last modified timestamp (should be in seconds to match oauth2-server)
                    /* istanbul ignore else */
                    if (typeof response.data.last_modified === 'undefined') {
                        var lastModified = Date.now();

                        // convert from milliseconds to seconds since expires_in from PHP oauth2-server is in seconds
                        lastModified = Math.floor(lastModified / 1000);

                        response.data.last_modified = lastModified;
                    }

                    // Deployment tokens
                    /* istanbul ignore else */
                    if (typeof response.data.deployment_tokens === 'undefined' || response.data.deployment_tokens === '') {
                        response.data.deployment_tokens = service.getDeploymentToken();
                    }

                    var oauth2Info = JSON.stringify(response.data);

                    // Store the oauth2 info to a cookie
                    var result = wdOauth2HttpHelper.setOauth2Info(oauth2Info);

                    /* istanbul ignore else */
                    if (result === true) {
                        result = service.setAccessTokenHeader();
                    }

                    return result;
                };

                /**
                 * Get Access Token
                 *
                 * @since 1.30.0
                 * @return {Boolean}
                 */
                service.getAccessToken = function() {

                    return wdOauth2HttpHelper.getAccessToken();

                };

                /**
                 * Get Refresh Token
                 *
                 * @since 1.30.0
                 * @return {String}
                 */
                service.getRefreshToken = function() {

                    return wdOauth2HttpHelper.getRefreshToken();

                };

                /**
                 * Get Deployment Token
                 *
                 * @since 1.30.0
                 * @return {String}
                 */
                service.getDeploymentToken = function() {

                    return wdOauth2HttpHelper.getDeploymentToken();

                };

                /**
                 * Process Response
                 *
                 * Check response for status of 4010
                 *
                 * @since 1.30.0
                 * @param {Object} response
                 * @return {Object}
                 */
                service.processResponse = function (response) {

                    var deferred = $q.defer();

                    if (response.status === 401) {

                        service.refreshAccessToken()
                            .then(function (refreshResponse) {

                                deferred.resolve(refreshResponse);

                            }, function(refreshResponse) {

                                deferred.reject(refreshResponse);

                            });

                    } else {

                        deferred.resolve(response);

                    }

                    return deferred.promise;

                };

                /**
                 * Get Wrapper
                 *
                 * @since 2.47.0
                 * @param {String} url
                 * @param {Object} cacheOptions
                 * @param {Object} config $http config options
                 * @return {Object}
                 */
                service.get = function (url, cacheOptions, config) {

                    var request = {
                        method: 'GET',
                        url: url
                    };

                    return service.processRequest(request, cacheOptions, config);

                };

                /**
                 * Put Wrapper
                 *
                 * @since 1.30.0
                 * @param {String} url
                 * @param {Object} data
                 * @return {Object}
                 */
                service.put = function (url, data) {

                    return service.processRequest({ method: 'PUT', url: url, data: data }, null);

                };

                /**
                 * Post Wrapper
                 *
                 * @since 1.30.0
                 * @param {String} url
                 * @param {Object} data
                 * @return {Object}
                 */
                service.post = function (url, data) {

                    return service.processRequest({ method: 'POST', url: url, data: data }, null);

                };

                /**
                 * Delete Wrapper
                 *
                 * @since 1.30.0
                 * @param {String} url
                 * @return {Object}
                 */
                service.delete = function (url) {

                    return service.processRequest({ method: 'DELETE', url: url }, null);

                };

                /**
                 * Check if wd.settings cookie version matches version from WD_SETTINGS_VERSION constant.
                 *
                 * @since 1.30.0
                 * @returns {Object}
                 */
                service.checkWdSettingsVersion = function () {

                    var result = {};

                    result.status = 2000;
                    result.statusText = 'Authentication Successful';

                    // Get the current wd.settings cookie version
                    var wdSettingsVersionCurrent = wdSettingsVersion;

                    // The wd.settings cookie version value in the cookie that was set when the user logged in via SSO
                    var wdSettingsVersionCookie = service.getWdSettingsVersion();

                    // Check if the cookie exists
                    if (wdSettingsVersionCookie === '') {
                        result.status = 4010;
                        result.statusText = 'Cookie Not Found! (Requires latest version [' + wdSettingsVersionCurrent + '])';
                        console.log(result.statusText);

                        window.location.replace('https://accounts.webduct.com/login');
                    }
                    // Check if wd.settings version is up to date
                    /* istanbul ignore else */
                    else if (wdSettingsVersionCookie !== wdSettingsVersionCurrent) {  // version in cookie differs from version set when user logged in
                        result.status = 4000;
                        result.statusText = 'Cookie version [' + wdSettingsVersionCookie + '] ' +
                            'does not match expected [' + wdSettingsVersionCurrent + ']';
                        console.log(result.statusText);
                    }

                    return result;

                };

                /**
                 * Process Request Wrapper
                 *
                 * @since 2.47.0
                 * @param {Object} request
                 * @param {Object} cacheOptions
                 * @param {Object} config $http config options
                 * @return {Object}
                 */
                service.processRequest = function(request, cacheOptions, config) {

                    var version = service.checkWdSettingsVersion();   // check wd.settings cookie version

                    var deferred = $q.defer();

                    if (version.status === 2000) {

                        bcxHttpService.processHttpRequest(request, cacheOptions, config)
                            .then(
                                function (response)
                                { // all good, status = 2000
                                    deferred.resolve(response);

                                }, function (response)
                                {

                                    if (response.status === 4010) { // try to refresh the access token

                                        service.refreshAccessToken()
                                            .then(
                                                function (response)
                                                {

                                                    // Add the newly refreshed access token for the retry request
                                                    var accessToken = response.data['access_token'];
                                                    addAuthorizationBearerHeader(accessToken);

                                                    service.retryRequest(request, accessToken)
                                                        .then(
                                                            function (response)
                                                            {
                                                                deferred.resolve(response);

                                                            }, function (response)
                                                            {
                                                                deferred.reject(response);
                                                            }
                                                        );

                                                }, function (response)
                                                {

                                                    // refresh failed
                                                    deferred.reject(response);

                                                }
                                            );

                                    } else {

                                        // non-4010 response
                                        deferred.reject(response);

                                    }
                                }
                            );
                    } else {
                        deferred.reject(version);
                    }

                    return deferred.promise;
                };

                /**
                 * Get the wd.settings version from the cookie
                 *
                 * @since 1.30.0
                 * @returns {String}
                 */
                service.getWdSettingsVersion = function() {

                    var result = '';

                    var cookieResult = service.cookieAdapter.getCookie(wdSettingsKey);

                    /* istanbul ignore else */
                    if (typeof cookieResult !== 'undefined') {

                        var cookieValue = JSON.parse(cookieResult);

                        /* istanbul ignore else */
                        if (typeof cookieValue.version !== 'undefined') {

                            result = cookieValue.version;

                        }
                    }

                    return result;

                };


                /**
                 * Retry Request
                 *
                 * @version 1.30.0
                 * @param {Object} request
                 * @param {String} accessToken
                 * @return {Object}
                 */
                service.retryRequest = function(request, accessToken) {

                    var deferred = $q.defer();

                    // Add the refreshed access token back to the header
                    /* istanbul ignore else */
                    if (typeof accessToken !== 'undefined') {
                        addAuthorizationBearerHeader(accessToken);
                    }

                    bcxHttpService.processHttpRequest(request)
                        .then(function (response) {

                            deferred.resolve(response);

                        }, function(response) {

                            deferred.reject(response);

                        });

                    return deferred.promise;

                };


                /**
                 * Refresh Access Token
                 *
                 * @since 1.30.0
                 * @return {Object}
                 */
                service.refreshAccessToken = function() {

                    var refreshData = {};
                    var deferred = $q.defer();

                    // see http://bshaffer.github.io/oauth2-server-php-docs/overview/grant-types/
                    refreshData.client_id = 'web-client';
                    refreshData.grant_type = 'refresh_token';
                    refreshData.refresh_token = service.getRefreshToken();

                    // avoid message Request header field Bearer is not allowed by Access-Control-Allow-Headers
                    bcxHttpService.deleteHeader('Bearer');

                    //POST for new access token
                    bcxHttpService.post(service.getOauth2Url(), refreshData)
                        .then(function(response) {

                            if (response.status.code === 200 ||
                                response.status.code === 204) {

                                service.storeOauth2Result(response);
                                deferred.resolve(response);

                            } else {

                                deferred.reject(response);

                            }

                        }, function(response) {

                            deferred.reject(response);

                        });

                    return deferred.promise;

                };

                /**
                 * Set the result from authenticating with the oauth2-server.
                 * Stores the access token, refresh token, and user info.
                 *
                 * @since 1.30.0
                 * @param {Object} response The response object from the oauth2-server
                 * @return {Boolean} Returns true
                 */
                service.storeOauth2Result = function(response) {

                    var result = true;

                    // Update oauth2 info
                    service.updateOauth2Info(response);

                    // Update user info
                    service.updateUserInfo(response);

                    return result;

                };

                /**
                 * Get oauth2 server URL.  Used for refreshing expired access token.
                 *
                 * @since 1.30.0
                 * @return {String} Returns the url of the oauth2 server
                 */
                service.getOauth2Url = function () {
                    return oauth2Url;
                };

                /**
                 * Set the oauth2 server URL.
                 *
                 * @since 1.30.0
                 * @param {String} url URL of the oauth2 server
                 * @return {Boolean} Returns true
                 */
                service.setOauth2Url = function (url) {
                    var result = true;
                    oauth2Url = url;

                    return result;
                };

                /**
                 * Set the single sign on URL.
                 *
                 * @since 1.30.0
                 * @param {String} url URL of the single sign on page
                 * @return {Boolean} Returns true
                 */
                service.setSingleSignOnUrl = function (url) {
                    var result = true;
                    singleSignOnUrl = url;

                    return result;
                };

                /**
                 * Set the oauth2 cookie options for the oauth2 client.
                 *
                 * @since 1.30.0
                 *
                 * @param {Object} options The oauth2 cookie options to set.
                 * @return {Boolean} Returns true
                 */
                service.setOauth2CookieOptions = function (options) {
                    var result = true;
                    wdOauth2HttpHelper.setOauth2CookieOptions(options);

                    return result;
                };

                /**
                 * Set the user info (to cookie). For example: the permissions and seat type index.
                 * This info will then be used by the wdPermission service.
                 *
                 * @since 1.30.0
                 * @param {Object} userInfo Object containing the user info to save to cookie
                 * @returns {Boolean}
                 */
                service.setUserInfoToCookie = function (userInfo) {

                    var settings = {};
                    var expireOptions = {};

                    var index = userInfo.id;
                    var permissions = parseInt(userInfo.permissions);
                    var seatTypeIndex = parseInt(userInfo['seat_type_id']);
                    var firstName = userInfo['first_name'];
                    var lastName = userInfo['last_name'];
                    var label = userInfo['first_name'] + ' ' + userInfo['last_name'];
                    var email = userInfo['email'];
                    var forceJobSelection = parseInt(userInfo['force_job_selection']);
                    var shopStandards = parseInt(userInfo['shop_standards']);

                    var cookieDomain = '';

                    /* istanbul ignore else */
                    if (typeof service.cookieOptions.domain !== 'undefined') {
                        cookieDomain = service.cookieOptions.domain;
                    }

                    // Check if the cookie domain is not the webduct.com domain
                    // If so, then we need to expire the cookie for the webduct.com domain to avoid conflicts
                    /* istanbul ignore else */
                    if (cookieDomain !== 'webduct.com') {
                        expireOptions = {
                            path: '/',
                            domain: 'webduct.com'
                        };

                        service.cookieAdapter.expireCookie(wdSettingsKey, expireOptions);
                    }

                    var expiresIn = 60 * 60 * 24 * 30;

                    var options = {
                        path: '/',
                        domain: cookieDomain,
                        expires: expiresIn,
                        secure: false
                    };

                    service.cookieAdapter.setOptions(options.path, options.domain, options.expires, options.secure);

                    var user = {};
                    user.index = index;
                    user.permissions = permissions;
                    user.seatTypeIndex = seatTypeIndex;
                    user.forceJobSelection = forceJobSelection;
                    user.shopStandards = shopStandards;
                    user.firstName = firstName;
                    user.lastName = lastName;
                    user.label = label;
                    user.email = email;

                    // Set the current version of the wd.settings cookie
                    settings.version = wdSettingsVersion;
                    settings.user = user;
                    settings = JSON.stringify(settings);

                    service.cookieAdapter.setCookie(wdSettingsKey, settings);

                };

                /**
                 * Store the user info from the result of calling the oauth2-server to a cookie.
                 *
                 * @since 1.30.0
                 * @param {Object} response The response from the oauth2-server authentication call
                 */
                service.updateUserInfo = function(response) {

                    var result;

                    /* istanbul ignore else */
                    if(response.data.user) {
                        result = service.setUserInfoToCookie(response.data.user);
                    }

                    return result;
                };

                /**
                 * Set the Webduct Settings cookie options
                 *
                 * @since 1.30.0
                 * @param {Object} options
                 * @returns {Boolean}
                 */
                service.setUserInfoCookieOptions = function(options) {
                    var result = true;

                    service.cookieOptions = options;

                    return result;
                };

                /******************************************************************************************************/

                /**
                 * Is Settings Cookie Defined
                 *
                 * @since 1.30.0
                 * @returns {Boolean}
                 */
                service.isSettingsCookieDefined = function() {

                    return typeof wdOauth2HttpHelper.getWdSettingsInfo() !== 'undefined';

                };

                return service;

            }]);

// EOF